<template>
  <div>
    <a v-if="showInviteLink" class="invite" @click="$emit('invite-enseignant')">{{
      $t("projetPage.infoAdministratives.participants.inviter")
    }}</a>
    <br />
    <!-- At least one enseignant must be associated to the projet -->
    <div v-if="!hasEnoughValidatedEnseignants">
      <!-- Hidden input to trigger a validation issue in correct section -->
      <div v-show="false">
        <InputWithValidation vid="participants_enseignants" rules="required" />
      </div>
      <b-message type="is-danger">
        {{ $t("projetPage.infoAdministratives.participants.at-least-one-enseignant") }}
      </b-message>
      <br />
    </div>
    <b-table
      :ref="tableRef"
      :striped="config.isStriped"
      :hoverable="false"
      :debounce-search="config.searchDebounce"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :per-page="config.perPage"
      :sticky-header="false"
      :current-page="currentPagedSortedParameter.pageNumber"
      :pagination-position="config.paginationPosition"
      backend-pagination
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      backend-sorting
      sort-multiple
      :sort-multiple-data="buefySortingPriority"
      :total="total"
      :data="enseignants"
      :loading="isLoading"
      @sort="onSort"
      @sorting-priority-removed="sortingPriorityRemoved"
      @page-change="onPageChange"
    >
      <b-table-column
        v-for="column in columns"
        :key="column.field"
        v-bind="column"
        :visible="column.visible"
      >
        <template #default="props">
          <div v-if="column.field === 'nouveau'">
            <b-icon
              v-if="isNewEnseignant(props.row.id)"
              icon="circle"
              size="is-small"
              type="is-primary"
            ></b-icon>
          </div>

          <div v-if="column.field === 'etablissement' && props.row.etablissement">
            {{ props.row.etablissement.nom }}
          </div>

          <div v-if="column.field === 'etablissement' && !props.row.etablissement" />

          <div v-if="column.field === 'prenom'">
            <div
              v-if="canSeeCourriel(props.row) && participating(props.row)"
              class="clickable"
              @click="showUserDetailsPage($event, props.row['id'])"
            >
              {{ props.row.prenom }}
            </div>
            <div v-else>
              {{
                $t("projetPage.infoAdministratives.participants.enseignantDe", [
                  props.row.etablissement ? props.row.etablissement.nom : $t("inconnu"),
                ])
              }}
            </div>
          </div>

          <div v-if="column.field === 'nom'">
            <div
              v-if="canSeeCourriel(props.row) && participating(props.row)"
              class="clickable"
              @click="showUserDetailsPage($event, props.row['id'])"
            >
              {{ props.row.nom }}
            </div>
            <div v-else>
              {{
                $t("projetPage.infoAdministratives.participants.enseignantDe", [
                  props.row.etablissement ? props.row.etablissement.nom : $t("inconnu"),
                ])
              }}
            </div>
          </div>

          <div v-if="column.field === 'courriel'">
            <div
              v-if="participating(props.row)"
              class="clickable"
              @click="showUserDetailsPage($event, props.row['id'])"
            >
              <!--only clickable if participating -->

              {{ treatCourriel(props.row, false) }}
            </div>
            <div v-else>
              {{ treatCourriel(props.row, true) }}
            </div>
          </div>

          <div v-if="column.field === 'participation'">
            <div v-if="participating(props.row)" class="participating">
              {{ $t("projetPage.infoAdministratives.participants.participe") }}
              <b-icon icon="check-circle" size="is-small" class="bullet-marker"></b-icon>
            </div>
            <div v-else-if="invited(props.row)" class="invited">
              {{ $t("projetPage.infoAdministratives.participants.invitationEnvoyee") }}
            </div>
          </div>

          <div v-if="column.field === 'action'">
            <b-button
              v-if="showInviteButton(props.row)"
              :disabled="readonly"
              type="is-primary is-outlined"
              @click="inviteToJoin(props.row)"
            >
              {{ $t("projetPage.infoAdministratives.participants.inviterCourt") }}
            </b-button>
            <b-button
              v-if="showQuitButton(props.row)"
              :disabled="readonly"
              type="is-danger is-outlined"
              @click="quit(props.row)"
            >
              {{ $t("projetPage.infoAdministratives.participants.quitter") }}
            </b-button>
            <b-button
              v-if="showRemove(props.row)"
              :disabled="readonly"
              type="is-warning is-outlined"
              @click="annuler(props.row)"
            >
              {{ $t("projetPage.infoAdministratives.participants.annuler") }}
            </b-button>
          </div>

          <div
            v-if="!column.custom"
            class="clickable"
            @click="showUserDetailsPage($event, props.row['id'])"
          >
            <div>{{ props.row[column.field] }}</div>
          </div>
        </template>
      </b-table-column>
    </b-table>

    <br />
    <br />
    <b-table
      :ref="tableRef"
      :data="etablissements"
      :striped="config.isStriped"
      :hoverable="false"
      :aria-next-label="config.nextLabel()"
      :aria-previous-label="config.previousLabel()"
      :aria-page-label="config.pageLabel()"
      :aria-current-label="config.currentPageLabel()"
      :sort-icon="config.sortIcon"
      :sort-icon-size="config.sortIconSize"
      :loading="isLoading"
      :class="{
        clickable: !isPorteurProjet,
      }"
      @click="!isPorteurProjet ? showEtablissementDetailsPage($event) : isPorteurProjet"
    >
      <b-table-column
        v-slot="props"
        field="name"
        label="Nom de l'établissement"
        width="120"
        sortable
      >
        {{ props.row.nom }}
      </b-table-column>
      <b-table-column
        v-slot="props"
        field="codeEtablissement"
        label="Numéro UAI"
        width="60"
        sortable
      >
        {{ props.row.codeEtablissement }}
      </b-table-column>
      <b-table-column v-slot="props" field="name" width="120" label="Adresse" sortable>
        {{ props.row.rue }}<br />
        {{ props.row.codePostal }} {{ props.row.ville }}
      </b-table-column>
    </b-table>
  </div>
</template>

<script lang="ts">
import Column from "@/views/tables/Column"
import {
  EtablissementDTO,
  FilterDTO,
  PaginationOrder,
  ProjetAnnuelDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
import ObfuscationUtils from "@/services/ObfuscationUtils"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { defineComponent } from "vue"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { eventBus } from "@/services/EventBus"
import { BButton, BIcon, BMessage, BTable, BTableColumn } from "buefy"

export default defineComponent({
  name: "ProjetEnseignantsView",
  components: {
    InputWithValidation,
    BButton,
    BMessage,
    BIcon,
    BTable,
    BTableColumn,
  },
  props: {
    projetAnnuel: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
    invitations: {
      type: Array as () => string[],
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showInviteLink: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "invite-enseignant",
    "update-enseignants",
    "invite-to-join-enseignant",
    "update-consentement-en-attente",
    "open-profile-modale",
    "dirtify-project",
    "trigger-validation",
  ],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      loggedUser: new UtilisateurDTO(),
      enseignants: [] as Array<UtilisateurDTO>,
      hasEnoughValidatedEnseignants: false,
      etablissements: [] as Array<EtablissementDTO>,

      // Hook initialized in created()
      tableView: null as any,
    }
  },
  computed: {
    tableRef(): string {
      return this.tableView?.state.tableRef || ""
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter || {}
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config || {}
    },
    activeFilters(): Array<any> {
      return this.tableView?.state.activeFilters || []
    },
  },
  watch: {
    "projetAnnuel.enseignants"(): void {
      this.reloadData()
    },
  },
  created(): void {
    this.tableView = useTableView({
      tableRef: "projet-enseignants-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: true,
    })

    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }
    this.loggedUser = loggedUser
  },
  mounted() {
    this.tableView.initMounted()
  },
  methods: {
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },

    updateTotal(total: number): void {
      this.tableView.updateTotal(total)
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      return new Array<FilterDTO>()
    },

    async reloadData(): Promise<void> {
      if (!this.projetAnnuel) {
        this.enseignants = []
        this.updateTotal(0)
        this.hasEnoughValidatedEnseignants = false
        this.etablissements = []
        return
      }
      this.enseignants = JSON.parse(JSON.stringify(this.projetAnnuel.enseignants))
      this.updateTotal(this.enseignants.length)
      this.hasEnoughValidatedEnseignants =
        this.projetAnnuel.enseignants.length - this.nbEnseignantEnAttente() > 0

      if (this.projetAnnuel && this.projetAnnuel.etablissements) {
        await this.sortEnseigantsAndAddInvitablesFromEtablissement()
      }
      this.$emit("trigger-validation")
    },

    annuler(enseignant: UtilisateurDTO): void {
      if (this.nbEnseignantEnAttente() > 0) {
        this.$buefy.dialog.confirm({
          message: this.$t(
            "projet.infos_administratives.areYouSureToRemoveInvitationUser2",
          ).toString(),
          confirmText: this.$t("confirmer").toString(),
          cancelText: this.$t("annuler").toString(),
          onConfirm: () => this.removeInvitation(enseignant),
        })
      } else {
        this.infoReportingService.dialog(
          this.$t("projet.infos_administratives.at-least-one-title", ["référent"]).toString(),
          this.$t("projet.infos_administratives.at-least-one-message", ["ce référent"]).toString(),
          "OK",
          () => {
            // Nothing to do
          },
          "is-warning",
        )
      }
    },
    removeInvitation(enseignant: UtilisateurDTO) {
      const updatedConsentementEnAttente = this.projetAnnuel.consentementEnAttente.filter(
        (element) => element !== enseignant.id,
      )
      this.$emit("update-consentement-en-attente", updatedConsentementEnAttente)
      this.removeUser(enseignant)
    },
    async sortEnseigantsAndAddInvitablesFromEtablissement(): Promise<void> {
      // Keep only enseigants that have accepted invitations
      const participatingEnseignants = this.enseignants.filter((ens) => {
        return this.participating(ens) && ens.id && ens.etablissement && ens.etablissement.id
      })
      // Get etablissements of these validated enseignants
      const etablissementIds: string[] = []
      this.etablissements = []
      participatingEnseignants.forEach((ens) => {
        if (
          ens.etablissement &&
          ens.etablissement.id &&
          etablissementIds.indexOf(ens.etablissement.id) < 0
        ) {
          etablissementIds.push(ens.etablissement.id)
          this.etablissements.push(ens.etablissement)
        }
      })

      // Sort
      const participatingEnseignantsIds = participatingEnseignants
        .filter((ens) => ens.id !== null && ens.id !== undefined)
        .map((ens) => ens.id)
      this.enseignants = this.enseignants.sort((ens1, ens2) => {
        // Me first
        if (ens1.id == this.loggedUser.id) {
          return -1
        }
        if (ens2.id == this.loggedUser.id) {
          return 1
        }

        // Then participating
        const ens1Participating = participatingEnseignantsIds.indexOf(ens1.id) > -1
        const ens2Participating = participatingEnseignantsIds.indexOf(ens2.id) > -1
        if (ens1Participating != ens2Participating) {
          return ens1Participating ? -1 : 1
        }

        // Sort per etablissement
        if (
          ens1.etablissement &&
          ens2.etablissement &&
          ens1.etablissement.nom != ens2.etablissement.nom
        ) {
          return ens1.etablissement.nom.localeCompare(ens2.etablissement.nom)
        }

        // Sort alphabetically then
        return ens1.courriel.localeCompare(ens2.courriel)
      })

      // Get all enseignants from these etablissements to propose to invite them
      if (etablissementIds.length > 0) {
        let others: UtilisateurDTO[] =
          await this.organismeService.getEnseignantsForEtablissements(etablissementIds)
        others = others.filter(
          (other) => !this.enseignants.some((present) => other.id === present.id),
        )

        this.enseignants = this.enseignants.concat(others)
      }
    },

    getInitialColumns(): Array<Column> {
      return [
        {
          field: "nouveau",
          label: this.$t("nouveau").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement",
          label: this.$t("graePage.membres.etablissement").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "prenom",
          label: this.$t("graePage.membres.prenom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "nom",
          label: this.$t("graePage.membres.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "courriel",
          label: this.$t("graePage.membres.courriel").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "participation",
          label: "",
          sortable: true,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "action",
          label: this.$t("projetPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
      ]
    },

    getDefaultSort(): Array<PaginationOrder> {
      const nameSort = new PaginationOrder()
      nameSort.clause = "nom"
      nameSort.desc = true
      return [nameSort]
    },

    showUserDetailsPage(event: Event, id: number): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      //For APP to open ProfileModale
      eventBus.emit("open-profile-modale", { show: true, id: id.toString() })
    },

    dirtifyProject(withRedirect: boolean): void {
      this.$emit("dirtify-project", withRedirect)
    },

    inviteToJoin(enseignant: UtilisateurDTO): void {
      this.$emit("invite-to-join-enseignant", enseignant)
      this.dirtifyProject(false)
    },

    quit(enseignant: UtilisateurDTO): void {
      if (this.projectHasMoreThanOneEnseignant()) {
        this.$buefy.dialog.confirm({
          message: this.$t(
            "projet.infos_administratives.areYouSureToRemoveInvitationUser2",
          ).toString(),
          confirmText: this.$t("confirmer").toString(),
          cancelText: this.$t("annuler").toString(),
          onConfirm: () => this.removeUser(enseignant),
        })
      } else {
        this.infoReportingService.dialog(
          this.$t("projet.infos_administratives.at-least-one-title", ["enseignant"]).toString(),
          this.$t("projet.infos_administratives.at-least-one-message", [
            "cet enseignant",
          ]).toString(),
          "OK",
          () => {
            // Nothing to do
          },
          "is-warning",
        )
      }
    },

    removeUser(enseignant: UtilisateurDTO) {
      const updatedEnseignants = this.projetAnnuel.enseignants.filter(
        (element) => element.id !== enseignant.id,
      )
      this.$emit("update-enseignants", updatedEnseignants)
      this.dirtifyProject(this.loggedUser.id === enseignant.id)
    },

    showInviteButton(toInvite: UtilisateurDTO): boolean {
      return (
        (this.isPorteurProjet ||
          this.usersService.isAccompagnateurRegional(this.loggedUser) ||
          this.usersService.isAccompagnateurNationalOrAdmin(this.loggedUser)) &&
        !this.isLinked(toInvite)
      )
    },

    showQuitButton(toDelete: UtilisateurDTO): boolean {
      return this.canDeleteEnseignants(toDelete) && !this.invitations.includes(toDelete.courriel)
    },

    canDeleteEnseignants(toDelete: UtilisateurDTO): boolean {
      return (
        (this.isPorteurProjet ||
          this.usersService.isAccompagnateurRegional(this.loggedUser) ||
          this.usersService.isAccompagnateurNationalOrAdmin(this.loggedUser)) &&
        this.isLinked(toDelete) &&
        !this.hasConsentementEnAttente(toDelete)
      )
    },

    isLinked(toDelete: UtilisateurDTO): boolean {
      return this.projetAnnuel.enseignants.some((utilisateur) => utilisateur.id === toDelete.id)
    },

    hasConsentementEnAttente(toDelete: UtilisateurDTO): boolean {
      return this.projetAnnuel.consentementEnAttente.some((id) => id === toDelete.id)
    },

    projectHasMoreThanOneEnseignant(): boolean {
      return this.projetAnnuel.enseignants.length - this.nbEnseignantEnAttente() > 1
    },

    nbEnseignantEnAttente(): number {
      return (
        this.projetAnnuel.consentementEnAttente.filter((id) =>
          this.projetAnnuel.enseignants.some((e) => e.id === id),
        ).length + this.invitations.length
      )
    },

    participating(user: UtilisateurDTO): boolean {
      if (user.id) {
        return (
          this.projetAnnuel.enseignants.some((enseignant) => enseignant.id === user.id) &&
          this.projetAnnuel.consentementEnAttente.indexOf(user.id) === -1 &&
          !this.invitations.includes(user.courriel)
        )
      } else {
        return false
      }
    },

    showRemove(user: UtilisateurDTO): boolean {
      const role = this.loggedUser.profil
      const roles = ["SUPER_ADMINISTRATEUR", "ACCOMPAGNATEUR_NATIONAL", "ANIMATEUR_REGIONAL"]
      if (user.id && roles.includes(role)) {
        return this.projetAnnuel.consentementEnAttente.indexOf(user.id) !== -1
      } else {
        return false
      }
    },

    invited(user: UtilisateurDTO): boolean {
      if (user.id) {
        return this.projetAnnuel.consentementEnAttente.indexOf(user.id) !== -1
      } else {
        return false
      }
    },

    isNewEnseignant(enseignantId: string): boolean {
      return this.projetAnnuel.nouveauxActeurs.some((acteur) => enseignantId === acteur.id)
    },

    treatCourriel(user: UtilisateurDTO, forceObfuscation: boolean): string {
      if (this.canSeeCourriel(user) && !forceObfuscation) {
        return user.courriel
      }
      return ObfuscationUtils.obfuscateCourriel(user.courriel)
    },

    canSeeCourriel(user: UtilisateurDTO): boolean {
      return (
        user.acceptNameVisibilityOnProtectedListing ||
        user.id === this.loggedUser.id ||
        ObfuscationUtils.hasRightToSeeCourriel(user, this.loggedUser)
      )
    },

    showEtablissementDetailsPage(et: EtablissementDTO): void {
      this.$router.push("/etablissement/" + et.id + "/details")
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/styles/_variables";

.participating {
  text-align: center;
  color: $green-v2;
}

.invited {
  text-align: center;
  color: $green-v2;
}

.clickable {
  cursor: pointer;
}

.invite {
  font-family: $font-family-title;
}
</style>
